import React, { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { reveal, prefersReducedMotion } from '../lib/motion';
import { HERO_VIDEO } from '../data/products';
import { whatsappLinkProps, GENERAL_ORDER_MESSAGE } from '../lib/whatsapp';

const PUB = process.env.PUBLIC_URL || '';
const POSTER_URL = `${PUB}/${HERO_VIDEO.poster}`;
const VIDEO_URL = `${PUB}/${HERO_VIDEO.src}`;

/**
 * The hero leads with a short film — the bottle waits in darkness, warm light
 * blooms across it while gold dust falls — and freezes on its final frame.
 * The poster IS that final frame, so the video fades in over it and, when it
 * ends (or never plays: reduced motion, save-data, autoplay denied), the
 * composition is identical.
 */
const Hero = () => {
  const textRef    = useRef(null);
  const taglineRef = useRef(null);
  const ctaRef     = useRef(null);
  const scrollY    = useRef(0);
  const [playFilm, setPlayFilm]   = useState(false);
  const [filmLive, setFilmLive]   = useState(false);

  // Decided after mount so the SSR-less first paint always has the poster.
  useEffect(() => {
    if (prefersReducedMotion()) return;
    if (navigator.connection?.saveData) return;
    setPlayFilm(true);
  }, []);

  useEffect(() => {
    const reduced = prefersReducedMotion();

    const ctx = gsap.context(() => {
      const letters = textRef.current?.querySelectorAll('.hero-letter');
      if (letters?.length) {
        reveal(letters,
          { opacity: 0, y: 36, skewY: 2 },
          { opacity: 1, y: 0, skewY: 0, stagger: 0.055, duration: 1.3, ease: 'power3.out', delay: 0.3 }
        );
      }

      reveal(taglineRef.current,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out', delay: 1.5 }
      );

      reveal(ctaRef.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 1.0, ease: 'power2.out', delay: 1.8 }
      );
    }, textRef);

    // One listener drives the copy fade. Writing styles is deferred to rAF so
    // the passive listener never forces a synchronous layout.
    let ticking = false;
    const applyScroll = () => {
      ticking = false;
      if (!textRef.current || reduced) return;
      const progress = Math.min(scrollY.current / (window.innerHeight * 0.5), 1);
      textRef.current.style.opacity = String(1 - progress);
      textRef.current.style.transform = `translateX(${-progress * 70}px)`;
    };

    const onScroll = () => {
      scrollY.current = window.scrollY;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(applyScroll);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      ctx.revert();
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden', background: 'var(--black)' }}
    >
      {/* Film: poster first, video layered over it once it actually plays */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <img
          src={POSTER_URL}
          alt=""
          aria-hidden="true"
          className="hero-media"
          fetchPriority="high"
          decoding="async"
        />
        {playFilm && (
          <video
            className="hero-media hero-film"
            style={{ opacity: filmLive ? 1 : 0 }}
            src={VIDEO_URL}
            poster={POSTER_URL}
            autoPlay
            muted
            playsInline
            preload="auto"
            onPlaying={() => setFilmLive(true)}
            data-testid="hero-video"
          />
        )}
        {/* Scrim keeps the overlay copy readable over the bright bottle. */}
        <div className="hero-scrim" aria-hidden="true" />
      </div>

      {/* HTML overlay */}
      <div
        ref={textRef}
        style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, pointerEvents: 'none', willChange: 'opacity, transform',
        }}
      >
        {/* Eyebrow */}
        <div className="hero-letter hero-eyebrow" style={{ opacity: 0, fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '11px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '18px' }}>
          Est.&nbsp;2025&nbsp;&nbsp;·&nbsp;&nbsp;Toronto,&nbsp;Ontario
        </div>

        {/* MAISON */}
        <div style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(14px, 2.5vw, 20px)', letterSpacing: '10px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '6px' }}>
          {'MAISON'.split('').map((ch, i) => (
            <span key={i} className="hero-letter" style={{ opacity: 0, display: 'inline-block' }}>{ch}</span>
          ))}
        </div>

        {/* VAEL */}
        <div data-testid="hero-title" style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(64px, 13vw, 112px)', letterSpacing: '14px', fontWeight: 400, color: 'var(--cream)', lineHeight: 1, marginBottom: '22px' }}>
          {'VAEL'.split('').map((ch, i) => (
            <span key={i} className="hero-letter" style={{ opacity: 0, display: 'inline-block' }}>{ch}</span>
          ))}
        </div>

        {/* Gold rule */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '22px' }}>
          <div style={{ width: '80px', height: '1px', background: 'rgba(201,169,110,0.35)' }} />
          <div style={{ width: '5px', height: '5px', background: 'var(--gold)', transform: 'rotate(45deg)' }} />
          <div style={{ width: '80px', height: '1px', background: 'rgba(201,169,110,0.35)' }} />
        </div>

        {/* Tagline */}
        <p
          ref={taglineRef}
          data-testid="hero-tagline"
          style={{
            fontFamily: 'Cormorant Garamond', fontStyle: 'italic',
            fontSize: 'clamp(16px, 2.2vw, 21px)', color: 'var(--grey)',
            letterSpacing: '4px', opacity: 0, margin: '0 0 48px 0',
          }}
        >
          The Art of Inspired Fragrance
        </p>

        {/* CTAs */}
        <div
          ref={ctaRef}
          style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', opacity: 0, pointerEvents: 'auto' }}
        >
          <a href="#collection" className="btn-gold" data-testid="hero-explore-btn">
            Explore Collection
          </a>
          <a
            {...whatsappLinkProps(GENERAL_ORDER_MESSAGE)}
            className="btn-gold"
            data-testid="hero-whatsapp-btn"
            style={{ borderColor: 'rgba(201,169,110,0.4)', color: 'rgba(201,169,110,0.75)' }}
          >
            Order via WhatsApp
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        data-testid="scroll-indicator"
        className="scroll-indicator"
        aria-hidden="true"
        style={{ position: 'absolute', bottom: '36px', left: '50%', zIndex: 10, pointerEvents: 'none' }}
      >
        <svg width="18" height="28" viewBox="0 0 18 28" fill="none">
          <rect x="1" y="1" width="16" height="26" rx="8" stroke="rgba(201,169,110,0.4)" strokeWidth="1" />
          <rect x="7" y="6" width="4" height="7" rx="2" fill="rgba(201,169,110,0.6)" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
